import React from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../components/forms/icon.component';

interface Action {
  label: string;
  icon: string;
  color?: string;
  onClick: () => void;
}

interface ActionsDropdownProps {
  edit?: () => void;
  editData?: () => void;
  send?: () => void;
  desactivateModal?: () => void;
  channelSettings?: () => void;
  activateModal?: () => void;
  changeStatus?: () => void;
  delete?: () => void;
  rowStatus?: boolean;
  options?: Action[];
  enableDownloads?: boolean;
  downloadOptions?: Action[];
  textCustomEdit?: string;
  textCustomEditData?: string;
}

const ActionsDropdown: React.FC<ActionsDropdownProps> = ({
  edit,
  send,
  editData,
  changeStatus,
  desactivateModal,
  activateModal,
  channelSettings,
  delete: deleteAction,
  rowStatus,
  options = [],
  enableDownloads = false,
  downloadOptions = [],
  textCustomEdit,
  textCustomEditData
}) => {
  const { t } = useTranslation();

  return (
    <div className="text-nowrap">
      <span
        className="btn btn-light-info fw-bolder btn-sm btn-flex btn-center"
        data-kt-menu-trigger="{default: 'click', lg: 'hover'}"
        data-kt-menu-attach="parent"
        data-kt-menu-placement="bottom-end"
      >
        {t('general.acctions')}
        <i className="ki-duotone ki-down fs-5 ms-1" />
      </span>
      <div
        className="menu menu-sub menu-sub-dropdown menu-column menu-rounded menu-gray-800 menu-state-bg-light-primary fw-semibold py-3 w-auto min-w-230px max-w-full text-nowrap"
        data-kt-menu="true"
      >
        <div className="menu-link text-muted fw-bolder px-3 ms-4 fs-7 text-uppercase d-flex justify-content-start pt-3">
          {t('general.acctions')}
        </div>
        {edit && (
          <div className="menu-item px-3">
            <button className="btn menu-link px-3 w-200px h-35px" type="button" onClick={edit}>
              <Icon icon="ki-pencil" size="fs-1" type="info" />
              <span className="ms-2">{textCustomEdit || t('general.edit')}</span>
            </button>
          </div>
        )}

        {editData && (
          <div className="menu-item px-3">
            <button className="btn menu-link px-3 w-250px h-35px" type="button" onClick={editData}>
              <Icon icon="ki-pencil" size="fs-1" type="info" />
              <span className="ms-2">{textCustomEditData || t('general.edit')}</span>
            </button>
          </div>
        )}

        {changeStatus && (
          <div className="menu-item px-3">
            <button
              className="btn menu-link px-3 w-200px h-35px"
              type="button"
              onClick={changeStatus}
            >
              <Icon
                icon={`${rowStatus ? 'ki-shield-cross' : 'ki-shield-tick'}`}
                size="fs-1"
                type={`${rowStatus ? 'danger' : 'primary'}`}
              />
              <span className="ms-2">
                {rowStatus ? t('general.desactivate') : t('general.activate')}
              </span>
            </button>
          </div>
        )}

        {desactivateModal && (
          <div className="menu-item px-3">
            <button
              className="btn menu-link px-3 w-200px h-35px"
              type="button"
              onClick={desactivateModal}
            >
              <Icon icon="ki-shield-cross" size="fs-1" type="danger" />
              <span className="ms-2">{t('general.desactivate')}</span>
            </button>
          </div>
        )}

        {activateModal && (
          <div className="menu-item px-3">
            <button
              className="btn menu-link px-3 w-200px h-35px"
              type="button"
              onClick={activateModal}
            >
              <Icon icon="ki-shield-tick" size="fs-1" type="primary" />
              <span className="ms-2">{t('general.activate')}</span>
            </button>
          </div>
        )}

        {channelSettings && (
          <div className="menu-item px-3">
            <button
              className="btn menu-link px-3 w-200px h-35px"
              type="button"
              onClick={channelSettings}
            >
              <Icon icon="ki-setting-2" size="fs-1" type="warning" />
              <span className="ms-2">{t('account-page.configure-channels')}</span>
            </button>
          </div>
        )}

        {options.map((option, index) => (
          <div key={index} className="menu-item px-3">
            <button
              className="btn menu-link px-3 w-200px h-35px"
              type="button"
              onClick={option.onClick}
            >
              <Icon icon={option.icon} size="fs-1" type={`${option.color || 'primary'}`} />
              <span className="ms-2">{option.label}</span>
            </button>
          </div>
        ))}

        {deleteAction && (
          <div className="menu-item px-3">
            <button
              className="btn menu-link px-3 w-200px h-35px"
              type="button"
              onClick={deleteAction}
            >
              <Icon icon="ki-trash" size="fs-1" type="danger" />
              <span className="ms-2">{t('general.delete')}</span>
            </button>
          </div>
        )}

        {send && (
          <div className="menu-item px-3">
            <button className="btn menu-link px-3 w-200px h-35px" type="button" onClick={send}>
              <Icon icon="ki-send" size="fs-1" type="primary" />
              <span className="ms-2">{t('general.test-message')}</span>
            </button>
          </div>
        )}

        {enableDownloads && downloadOptions.length > 0 && (
          <>
            <div className="menu-link text-muted fw-bolder px-3 ms-4 fs-7 mt-2 text-uppercase d-flex justify-content-start">
              {t('general.downloads')}
            </div>
            {downloadOptions.map((option, index) => (
              <div key={index} className="menu-item px-3">
                <button
                  className="btn menu-link px-3 w-200px h-35px"
                  type="button"
                  onClick={option.onClick}
                >
                  <Icon icon={option.icon} size="fs-1" type={`${option.color || ''}`} />
                  <span className="ms-2">{option.label}</span>
                </button>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
};

export default ActionsDropdown;
